import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert, Trash2, UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function MembersPage() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list(), enabled: isAdmin });
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: api.users.list, enabled: isAdmin });
  const [projectId, setProjectId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");

  const { data: members } = useQuery({ queryKey: ["members", projectId], queryFn: () => api.members.list(Number(projectId)), enabled: isAdmin && !!projectId });
  const inval = () => qc.invalidateQueries({ queryKey: ["members"] });
  const addMut = useMutation({ mutationFn: api.members.add, onSuccess: () => { toast.success("Membru adăugat"); inval(); setSelectedUserId(""); }, onError: (e) => toast.error(apiError(e)) });
  const removeMut = useMutation({ mutationFn: ({ projectId, userId }) => api.members.remove(projectId, userId), onSuccess: () => { toast.success("Membru eliminat"); inval(); }, onError: (e) => toast.error(apiError(e)) });

  if (!isAdmin) {
    return <DashboardLayout><div className="max-w-2xl mx-auto mt-12"><Card><CardContent className="flex flex-col items-center gap-3 py-12 text-center"><ShieldAlert className="h-10 w-10 text-muted-foreground" /><p className="text-lg font-medium">Acces restricționat</p><p className="text-sm text-muted-foreground">Doar administratorii pot gestiona membrii șantierelor.</p></CardContent></Card></div></DashboardLayout>;
  }

  const memberIds = new Set((members ?? []).map((m) => m.userId));
  const availableUsers = (users ?? []).filter((u) => !memberIds.has(u.id));

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="members-page">
        <div>
          <h1 className="text-2xl font-semibold">Membri șantiere</h1>
          <p className="text-sm text-muted-foreground">Asignați operatorii la șantiere. Un operator vede doar șantierele unde este membru.</p>
        </div>

        <Card>
          <CardHeader><CardTitle>Selectați șantierul</CardTitle></CardHeader>
          <CardContent>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="w-full max-w-md" data-testid="member-project-select"><SelectValue placeholder="Selectați un șantier" /></SelectTrigger>
              <SelectContent>{(projects ?? []).map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </CardContent>
        </Card>

        {projectId && (
          <>
            <Card>
              <CardHeader><CardTitle>Adaugă membru nou</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[260px]">
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger><SelectValue placeholder="Selectați un utilizator" /></SelectTrigger>
                    <SelectContent>
                      {availableUsers.map((u) => <SelectItem key={u.id} value={String(u.id)}>{u.name} ({u.email})</SelectItem>)}
                      {availableUsers.length === 0 && <SelectItem value="none" disabled>Toți utilizatorii sunt deja membri</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <Button disabled={!selectedUserId || selectedUserId === "none" || addMut.isPending} onClick={() => addMut.mutate({ projectId: Number(projectId), userId: Number(selectedUserId), roleInProject: "operator" })}><UserPlus className="h-4 w-4 mr-2" /> Adaugă</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>{members?.length ?? 0} membri</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Nume</TableHead><TableHead>Email</TableHead><TableHead>Rol global</TableHead><TableHead>Rol în șantier</TableHead><TableHead className="text-right">Acțiuni</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(members ?? []).map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">{m.userName || "-"}</TableCell>
                        <TableCell>{m.userEmail || "-"}</TableCell>
                        <TableCell><Badge variant={m.userRole === "admin" ? "default" : "secondary"}>{m.userRole === "admin" ? "Admin" : "Operator"}</Badge></TableCell>
                        <TableCell>{m.roleInProject}</TableCell>
                        <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => removeMut.mutate({ projectId: Number(projectId), userId: m.userId })}><Trash2 className="h-4 w-4 mr-2" /> Elimină</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
